"use client";

import { Divider } from "@/components/core/Divider";
import { AnonymousProfileModal } from "@/components/domain/voting/AnonymousProfileModal";
import { AnonymousProfileToggle } from "@/components/domain/voting/comments/AnonymousProfileToggle";
import { CommentButton } from "@/components/domain/voting/comments/CommentButton";
import { CommentInput } from "@/components/domain/voting/comments/CommentInput";
import { LoveReactionButton } from "@/components/domain/voting/comments/LoveReactionButton";
import { ReplyInput } from "@/components/domain/voting/comments/ReplyInput";
import { ThreadLine } from "@/components/domain/voting/comments/ThreadLine";
import { useAnonymousProfile } from "@/components/domain/voting/hooks/useAnonymousProfile";
import { useCommentDeletion } from "@/components/domain/voting/hooks/useCommentDeletion";
import { useAuth } from "@/hooks/useAuth";
import { useContentComments } from "@/hooks/useContentComments";
import { useContentDetailModal } from "@/hooks/useContentDetailModal";
import { cn } from "@/lib/utils";
import { ContentComment } from "@/types/content-comment";
import { VoteComment } from "@/types/vote";
import { AnonymousProfile } from "@/utils/anonymousProfile";
import { buildContentCommentTree } from "@/utils/contentCommentTreeBuilder";
import { formatDate } from "@/utils/formatDate";
import { renderTextWithMentions } from "@/utils/renderMentions";
import Avatar from "boring-avatars";
import { ChevronDownIcon, ChevronUpIcon, UndoIcon, XIcon } from "lucide-react";
import Image from "next/image";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

interface ContentCommentSectionProps {
    contentId: string;
    className?: string;
    hideTopDivider?: boolean;
}

// 최대 시각적 깊이 (2레벨 중첩: 0~1)
const MAX_VISUAL_DEPTH = 1;
const INITIAL_VISIBLE_REPLIES = 3;

/**
 * ContentComment를 VoteComment로 변환 (ReplyInput 호환용)
 */
const toVoteComment = (comment: ContentComment): VoteComment => ({
    ...comment,
    reply_count: comment.reply_count || 0,
    replies: comment.replies?.map(toVoteComment)
});

/**
 * 콘텐츠 댓글 아이템 컴포넌트
 */
const ContentCommentItem = ({
    comment,
    isMyComment,
    isPendingDelete,
    secondsRemaining,
    onDelete,
    onRevertDelete,
    isReplying,
    onReplyToggle,
    isSubmitting,
    onToggleReaction,
    isReactionLoading
}: {
    comment: ContentComment;
    isMyComment: boolean;
    isPendingDelete: boolean;
    secondsRemaining: number | null;
    onDelete: (commentId: string) => void;
    onRevertDelete: (commentId: string) => void;
    isReplying?: boolean;
    onReplyToggle?: () => void;
    isSubmitting?: boolean;
    onToggleReaction?: (commentId: string) => void;
    isReactionLoading?: boolean;
}) => {
    const showAnonymous =
        comment.is_anonymous &&
        comment.anonymous_nickname &&
        comment.anonymous_avatar_seed;

    const user = comment.mix_user || {
        id: "deleted",
        name: "삭제된 사용자",
        profileImg: ""
    };

    const displayName = showAnonymous ? comment.anonymous_nickname! : user.name;
    const avatarSeed = showAnonymous ? comment.anonymous_avatar_seed! : null;

    const formatCommentDate = (dateString: string) => {
        try {
            return formatDate(new Date(dateString));
        } catch {
            return dateString;
        }
    };

    return (
        <div
            className={cn(
                `mb-2 flex gap-3 rounded-lg border border-gray-200 bg-gray-50
                p-2 transition-all duration-300 dark:border-gray-700
                dark:bg-gray-900`
            )}
        >
            <div
                className={cn(
                    `flex items-start justify-center transition-all
                    duration-300`,
                    isPendingDelete && "opacity-60 blur-sm"
                )}
            >
                {showAnonymous ? (
                    <Avatar
                        name={avatarSeed ?? undefined}
                        variant="beam"
                        size={40}
                    />
                ) : user.profileImg ? (
                    <Image
                        src={user.profileImg}
                        alt={user.name}
                        className="rounded-full object-cover"
                        width={40}
                        height={40}
                    />
                ) : (
                    <div
                        className="flex h-10 w-10 items-center justify-center
                            rounded-full bg-gray-300 font-semibold text-gray-600
                            dark:bg-gray-700 dark:text-gray-300"
                    >
                        {user.name.charAt(0)}
                    </div>
                )}
            </div>

            <div className="min-w-0 flex-1">
                <div className="mb-1 flex items-center justify-between">
                    <div
                        className={cn(
                            `flex items-center gap-2 transition-all
                            duration-300`,
                            isPendingDelete && "opacity-60 blur-sm"
                        )}
                    >
                        <span
                            className="text-sm font-semibold text-gray-900
                                dark:text-white"
                        >
                            {displayName}
                        </span>
                        {showAnonymous && (
                            <span
                                className="rounded bg-gray-200 px-1.5 py-0.5
                                    text-xs text-gray-600 dark:bg-gray-700
                                    dark:text-gray-400"
                            >
                                익명
                            </span>
                        )}
                        <span
                            className="text-xs text-gray-500 dark:text-gray-400"
                        >
                            {formatCommentDate(comment.created_at)}
                        </span>
                    </div>

                    {isMyComment &&
                        !comment.is_anonymous &&
                        (isPendingDelete ? (
                            <button
                                onClick={() => onRevertDelete(comment.id)}
                                className="flex items-center gap-1.5 rounded-md
                                    bg-red-50 px-3 py-1.5 text-sm font-medium
                                    text-red-600 shadow-sm transition-all
                                    hover:bg-red-100 active:scale-95
                                    dark:bg-red-900/20 dark:text-red-400
                                    dark:hover:bg-red-900/40"
                                title="삭제 취소"
                            >
                                <UndoIcon className="size-4" />
                                <span>{secondsRemaining}초</span>
                            </button>
                        ) : (
                            <button
                                onClick={() => onDelete(comment.id)}
                                className="p-1.5 text-gray-400 transition-colors
                                    hover:text-red-500"
                                title="댓글 삭제"
                            >
                                <XIcon className="size-4" />
                            </button>
                        ))}
                </div>

                <p
                    className={cn(
                        `whitespace-pre-wrap break-words text-sm text-gray-700
                        transition-all duration-300 dark:text-gray-300`,
                        isPendingDelete && "opacity-60 blur-sm"
                    )}
                >
                    {renderTextWithMentions(comment.text)}
                </p>

                {/* 액션 버튼들 (좋아요, 답글) */}
                <div className="mt-2 flex items-center gap-2">
                    {onToggleReaction && (
                        <LoveReactionButton
                            checked={comment.hasReacted || false}
                            count={comment.reaction_count || 0}
                            onChange={() => onToggleReaction(comment.id)}
                            disabled={isReactionLoading}
                            size="sm"
                        />
                    )}
                    {onReplyToggle && (
                        <CommentButton
                            active={isReplying}
                            count={
                                comment.reply_count ||
                                comment.replies?.length ||
                                0
                            }
                            onClick={onReplyToggle}
                            disabled={isSubmitting}
                            size="sm"
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

/**
 * 콘텐츠 댓글 스레드 컴포넌트 (재귀)
 */
const ContentCommentThread = ({
    comment,
    depth,
    maxVisualDepth = MAX_VISUAL_DEPTH,
    myUserId,
    isAnonymousMode,
    anonymousProfile,
    user,
    onReply,
    onDelete,
    onRevertDelete,
    isPendingDelete,
    getSecondsRemaining,
    isSubmitting,
    onToggleReaction,
    reactionLoadingId,
    onSignIn,
    isLastChild = false
}: {
    comment: ContentComment;
    depth: number;
    maxVisualDepth?: number;
    myUserId?: string;
    isAnonymousMode: boolean;
    anonymousProfile: AnonymousProfile | null;
    user: { id: string; name: string; profileImg: string } | null;
    onReply: (parentId: string, text: string) => Promise<void>;
    onDelete: (commentId: string) => void;
    onRevertDelete: (commentId: string) => void;
    isPendingDelete: (commentId: string) => boolean;
    getSecondsRemaining: (commentId: string) => number | null;
    isSubmitting: boolean;
    onToggleReaction?: (commentId: string) => void;
    reactionLoadingId?: string | null;
    onSignIn: () => void;
    isLastChild?: boolean;
}) => {
    const [isReplying, setIsReplying] = useState(false);
    const [showAllReplies, setShowAllReplies] = useState(false);

    const handleReplyToggle = useCallback(() => {
        if (!user) {
            onSignIn();
            return;
        }
        setIsReplying(!isReplying);
    }, [user, onSignIn, isReplying]);

    const hasReplies = comment.replies && comment.replies.length > 0;
    const canRenderReplies = depth < maxVisualDepth && hasReplies;

    const isMyComment = myUserId
        ? comment.is_anonymous
            ? false
            : comment.mix_user?.id === myUserId
        : false;

    const handleReplySubmit = useCallback(
        async (text: string) => {
            await onReply(comment.id, text);
            setIsReplying(false);
        },
        [comment.id, onReply]
    );

    const getMentionUsername = () => {
        if (depth < maxVisualDepth) return undefined;
        if (comment.is_anonymous && comment.anonymous_nickname) {
            return comment.anonymous_nickname;
        }
        return comment.mix_user?.name;
    };

    const visibleReplies = showAllReplies
        ? comment.replies || []
        : (comment.replies || []).slice(0, INITIAL_VISIBLE_REPLIES);

    const hasMoreReplies =
        !showAllReplies &&
        (comment.replies?.length || 0) > INITIAL_VISIBLE_REPLIES;
    const hiddenRepliesCount =
        (comment.replies?.length || 0) - INITIAL_VISIBLE_REPLIES;

    // depth > 0: 스레드 라인 포함
    if (depth > 0) {
        return (
            <div className="flex">
                <ThreadLine isLastChild={isLastChild} />
                <div className="min-w-0 flex-1">
                    <ContentCommentItem
                        comment={comment}
                        isMyComment={isMyComment}
                        isPendingDelete={isPendingDelete(comment.id)}
                        secondsRemaining={getSecondsRemaining(comment.id)}
                        onDelete={onDelete}
                        onRevertDelete={onRevertDelete}
                        isReplying={isReplying}
                        onReplyToggle={
                            depth < maxVisualDepth
                                ? handleReplyToggle
                                : undefined
                        }
                        isSubmitting={isSubmitting}
                        onToggleReaction={onToggleReaction}
                        isReactionLoading={reactionLoadingId === comment.id}
                    />

                    {isReplying && (
                        <ReplyInput
                            parentComment={toVoteComment(comment)}
                            isAnonymousMode={isAnonymousMode}
                            anonymousProfile={anonymousProfile}
                            user={user}
                            isSubmitting={isSubmitting}
                            onSubmit={handleReplySubmit}
                            onCancel={() => setIsReplying(false)}
                            mentionUsername={getMentionUsername()}
                        />
                    )}

                    {canRenderReplies && (
                        <div>
                            {visibleReplies.map((reply, index) => (
                                <ContentCommentThread
                                    key={reply.id}
                                    comment={reply}
                                    depth={depth + 1}
                                    maxVisualDepth={maxVisualDepth}
                                    myUserId={myUserId}
                                    isAnonymousMode={isAnonymousMode}
                                    anonymousProfile={anonymousProfile}
                                    user={user}
                                    onReply={onReply}
                                    onDelete={onDelete}
                                    onRevertDelete={onRevertDelete}
                                    isPendingDelete={isPendingDelete}
                                    getSecondsRemaining={getSecondsRemaining}
                                    isSubmitting={isSubmitting}
                                    onToggleReaction={onToggleReaction}
                                    reactionLoadingId={reactionLoadingId}
                                    onSignIn={onSignIn}
                                    isLastChild={
                                        showAllReplies
                                            ? index ===
                                              (comment.replies?.length || 0) - 1
                                            : index ===
                                                  visibleReplies.length - 1 &&
                                              !hasMoreReplies
                                    }
                                />
                            ))}

                            {hasMoreReplies && (
                                <div className="flex">
                                    <ThreadLine />
                                    <button
                                        onClick={() => setShowAllReplies(true)}
                                        className="flex items-center gap-1.5
                                            py-1.5 text-sm font-medium
                                            text-blue-600 transition-colors
                                            hover:text-blue-700
                                            dark:text-blue-400
                                            dark:hover:text-blue-300"
                                    >
                                        <ChevronDownIcon className="size-4" />
                                        <span>
                                            답글 {hiddenRepliesCount}개 더 보기
                                        </span>
                                    </button>
                                </div>
                            )}

                            {showAllReplies &&
                                (comment.replies?.length || 0) >
                                    INITIAL_VISIBLE_REPLIES && (
                                    <button
                                        onClick={() => setShowAllReplies(false)}
                                        className="ml-4 flex items-center
                                            gap-1.5 py-1.5 text-sm font-medium
                                            text-gray-500 transition-colors
                                            hover:text-gray-700
                                            dark:text-gray-400
                                            dark:hover:text-gray-300"
                                    >
                                        <ChevronUpIcon className="size-4" />
                                        <span>답글 접기</span>
                                    </button>
                                )}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // depth === 0: 최상위 댓글
    return (
        <div className="mb-3">
            <ContentCommentItem
                comment={comment}
                isMyComment={isMyComment}
                isPendingDelete={isPendingDelete(comment.id)}
                secondsRemaining={getSecondsRemaining(comment.id)}
                onDelete={onDelete}
                onRevertDelete={onRevertDelete}
                isReplying={isReplying}
                onReplyToggle={handleReplyToggle}
                isSubmitting={isSubmitting}
                onToggleReaction={onToggleReaction}
                isReactionLoading={reactionLoadingId === comment.id}
            />

            {isReplying && (
                <ReplyInput
                    parentComment={toVoteComment(comment)}
                    isAnonymousMode={isAnonymousMode}
                    anonymousProfile={anonymousProfile}
                    user={user}
                    isSubmitting={isSubmitting}
                    onSubmit={handleReplySubmit}
                    onCancel={() => setIsReplying(false)}
                    mentionUsername={getMentionUsername()}
                />
            )}

            {canRenderReplies && (
                <div className="ml-4">
                    {visibleReplies.map((reply, index) => (
                        <ContentCommentThread
                            key={reply.id}
                            comment={reply}
                            depth={depth + 1}
                            maxVisualDepth={maxVisualDepth}
                            myUserId={myUserId}
                            isAnonymousMode={isAnonymousMode}
                            anonymousProfile={anonymousProfile}
                            user={user}
                            onReply={onReply}
                            onDelete={onDelete}
                            onRevertDelete={onRevertDelete}
                            isPendingDelete={isPendingDelete}
                            getSecondsRemaining={getSecondsRemaining}
                            isSubmitting={isSubmitting}
                            onToggleReaction={onToggleReaction}
                            reactionLoadingId={reactionLoadingId}
                            onSignIn={onSignIn}
                            isLastChild={
                                showAllReplies
                                    ? index ===
                                      (comment.replies?.length || 0) - 1
                                    : index === visibleReplies.length - 1 &&
                                      !hasMoreReplies
                            }
                        />
                    ))}

                    {hasMoreReplies && (
                        <div className="flex">
                            <ThreadLine />
                            <button
                                onClick={() => setShowAllReplies(true)}
                                className="flex items-center gap-1.5 py-1.5
                                    text-sm font-medium text-blue-600
                                    transition-colors hover:text-blue-700
                                    dark:text-blue-400 dark:hover:text-blue-300"
                            >
                                <ChevronDownIcon className="size-4" />
                                <span>답글 {hiddenRepliesCount}개 더 보기</span>
                            </button>
                        </div>
                    )}

                    {showAllReplies &&
                        (comment.replies?.length || 0) >
                            INITIAL_VISIBLE_REPLIES && (
                            <button
                                onClick={() => setShowAllReplies(false)}
                                className="ml-4 flex items-center gap-1.5 py-1.5
                                    text-sm font-medium text-gray-500
                                    transition-colors hover:text-gray-700
                                    dark:text-gray-400 dark:hover:text-gray-300"
                            >
                                <ChevronUpIcon className="size-4" />
                                <span>답글 접기</span>
                            </button>
                        )}
                </div>
            )}
        </div>
    );
};

/**
 * 콘텐츠 댓글 목록 컴포넌트
 */
const ContentCommentList = ({
    comments,
    isLoading,
    myUserId,
    isAnonymousMode,
    anonymousProfile,
    user,
    isPending,
    getSecondsRemaining,
    onReply,
    onDeleteComment,
    onRevertDelete,
    onToggleReaction,
    reactionLoadingId,
    onSignIn
}: {
    comments: ContentComment[];
    isLoading: boolean;
    myUserId?: string;
    isAnonymousMode: boolean;
    anonymousProfile: AnonymousProfile | null;
    user: { id: string; name: string; profileImg: string } | null;
    isPending: (commentId: string) => boolean;
    getSecondsRemaining: (commentId: string) => number | null;
    onReply: (parentCommentId: string, text: string) => Promise<void>;
    onDeleteComment: (commentId: string) => void;
    onRevertDelete: (commentId: string) => void;
    onToggleReaction?: (commentId: string) => void;
    reactionLoadingId?: string | null;
    onSignIn: () => void;
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [animatedCommentIds, setAnimatedCommentIds] = useState<Set<string>>(
        new Set()
    );
    const isInitialMount = useRef(true);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            const timer = setTimeout(() => {
                setAnimatedCommentIds(new Set(comments.map((c) => c.id)));
            }, 1000);
            return () => clearTimeout(timer);
        } else {
            const newCommentIds = comments
                .filter((c) => !animatedCommentIds.has(c.id))
                .map((c) => c.id);
            if (newCommentIds.length > 0) {
                const timer = setTimeout(() => {
                    setAnimatedCommentIds(
                        (prev) =>
                            new Set([...Array.from(prev), ...newCommentIds])
                    );
                }, 500);
                return () => clearTimeout(timer);
            }
        }
    }, [comments, animatedCommentIds]);

    if (isLoading) {
        return (
            <div
                className="py-8 text-center text-sm text-gray-500
                    dark:text-gray-400"
            >
                댓글을 불러오는 중...
            </div>
        );
    }

    if (comments.length === 0) {
        return (
            <div className="py-8 text-center text-sm text-black dark:text-white">
                아직 댓글이 없습니다
            </div>
        );
    }

    const handleReply = async (parentCommentId: string, text: string) => {
        setIsSubmitting(true);
        try {
            await onReply(parentCommentId, text);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="space-y-0">
            {comments.map((comment, index) => {
                const shouldAnimate = !animatedCommentIds.has(comment.id);
                const delayClass =
                    isInitialMount.current && index < 5
                        ? index === 0
                            ? ""
                            : `animate-comment-enter-delay-${Math.min(
                                  index,
                                  4
                              )}`
                        : "";
                const animationClass = shouldAnimate
                    ? delayClass || "animate-comment-enter"
                    : "";

                return (
                    <div key={comment.id} className={animationClass}>
                        <ContentCommentThread
                            comment={comment}
                            depth={0}
                            maxVisualDepth={1}
                            myUserId={myUserId}
                            isAnonymousMode={isAnonymousMode}
                            anonymousProfile={anonymousProfile}
                            user={user}
                            onReply={handleReply}
                            onDelete={onDeleteComment}
                            onRevertDelete={onRevertDelete}
                            isPendingDelete={isPending}
                            getSecondsRemaining={getSecondsRemaining}
                            isSubmitting={isSubmitting}
                            onToggleReaction={onToggleReaction}
                            reactionLoadingId={reactionLoadingId}
                            onSignIn={onSignIn}
                        />
                    </div>
                );
            })}
        </div>
    );
};

/**
 * 콘텐츠 댓글 섹션 메인 컴포넌트
 * n-level 중첩 댓글 + 좋아요 반응 지원
 */
export const ContentCommentSection = ({
    contentId,
    className,
    hideTopDivider = false
}: ContentCommentSectionProps) => {
    const { me, isLoggedIn, openSignInModal } = useAuth();
    const { saveContentUrlForRedirect } = useContentDetailModal();
    const {
        comments,
        totalCount,
        isLoading,
        addComment,
        deleteComment,
        toggleReaction,
        removeReaction
    } = useContentComments({ contentId });

    const {
        isEnabled: isAnonymousModeEnabled,
        profile: anonymousProfile,
        toggleEnabled,
        regenerateProfile,
        setProfile
    } = useAnonymousProfile();

    const [showProfileModal, setShowProfileModal] = useState(false);

    const { scheduleDelete, revertDelete, isPending, getSecondsRemaining } =
        useCommentDeletion(deleteComment);

    // 낙관적 업데이트를 위한 로컬 상태
    const [optimisticReactions, setOptimisticReactions] = useState<
        Record<string, { hasReacted: boolean; countDelta: number }>
    >({});

    // 익명 모드 ON + 프로필 없으면 모달 표시
    useEffect(() => {
        if (isLoggedIn && isAnonymousModeEnabled && !anonymousProfile) {
            setShowProfileModal(true);
        }
    }, [isLoggedIn, isAnonymousModeEnabled, anonymousProfile]);

    // 평탄한 댓글 배열을 트리 구조로 변환 + 낙관적 업데이트 적용
    const commentTree = useMemo(() => {
        const tree = buildContentCommentTree(comments);
        const addOptimisticData = (
            commentList: ContentComment[]
        ): ContentComment[] =>
            commentList.map((comment) => {
                const optimistic = optimisticReactions[comment.id];
                const hasReacted =
                    optimistic !== undefined
                        ? optimistic.hasReacted
                        : (comment.hasReacted ?? false);
                const reactionCount =
                    (comment.reaction_count || 0) +
                    (optimistic?.countDelta || 0);
                return {
                    ...comment,
                    hasReacted,
                    reaction_count: reactionCount,
                    replies: comment.replies
                        ? addOptimisticData(comment.replies)
                        : undefined
                };
            });
        return addOptimisticData(tree);
    }, [comments, optimisticReactions]);

    // 최상위 댓글 제출 핸들러
    const handleSubmitComment = useCallback(
        async (text: string) => {
            if (isAnonymousModeEnabled && !anonymousProfile) {
                setShowProfileModal(true);
                return;
            }

            await addComment(
                text,
                {
                    isAnonymous: isAnonymousModeEnabled,
                    anonymousNickname: anonymousProfile?.nickname,
                    anonymousAvatarSeed: anonymousProfile?.avatarSeed
                },
                undefined
            );
        },
        [addComment, isAnonymousModeEnabled, anonymousProfile]
    );

    // 답글 제출 핸들러
    const handleSubmitReply = useCallback(
        async (parentCommentId: string, text: string) => {
            if (isAnonymousModeEnabled && !anonymousProfile) {
                setShowProfileModal(true);
                return;
            }

            await addComment(
                text,
                {
                    isAnonymous: isAnonymousModeEnabled,
                    anonymousNickname: anonymousProfile?.nickname,
                    anonymousAvatarSeed: anonymousProfile?.avatarSeed
                },
                parentCommentId
            );
        },
        [addComment, isAnonymousModeEnabled, anonymousProfile]
    );

    // 좋아요 토글 핸들러 (낙관적 업데이트)
    const handleToggleReaction = useCallback(
        async (commentId: string) => {
            if (!isLoggedIn) {
                // 로그인 후 콘텐츠 모달로 돌아오기 위해 URL 저장
                saveContentUrlForRedirect();
                openSignInModal();
                return;
            }

            const currentOptimistic = optimisticReactions[commentId];

            // 트리에서 현재 hasReacted 값 찾기
            const findHasReacted = (
                commentList: ContentComment[]
            ): boolean | undefined => {
                for (const c of commentList) {
                    if (c.id === commentId) return c.hasReacted;
                    if (c.replies) {
                        const found = findHasReacted(c.replies);
                        if (found !== undefined) return found;
                    }
                }
                return undefined;
            };

            const currentHasReacted = findHasReacted(commentTree) ?? false;
            const newReaction = !currentHasReacted;
            const countDelta = newReaction ? 1 : -1;

            // 낙관적 업데이트
            setOptimisticReactions((prev) => ({
                ...prev,
                [commentId]: {
                    hasReacted: newReaction,
                    countDelta:
                        (currentOptimistic?.countDelta || 0) + countDelta
                }
            }));

            try {
                if (newReaction) {
                    await toggleReaction(commentId);
                } else {
                    await removeReaction(commentId);
                }
                // 성공 시 낙관적 상태 제거
                setOptimisticReactions((prev) => {
                    const { [commentId]: _, ...rest } = prev;
                    return rest;
                });
            } catch (error) {
                console.error("Failed to toggle reaction:", error);
                // 실패 시 롤백
                setOptimisticReactions((prev) => {
                    if (currentOptimistic) {
                        return { ...prev, [commentId]: currentOptimistic };
                    }
                    const { [commentId]: _, ...rest } = prev;
                    return rest;
                });
            }
        },
        [
            isLoggedIn,
            openSignInModal,
            saveContentUrlForRedirect,
            commentTree,
            optimisticReactions,
            toggleReaction,
            removeReaction
        ]
    );

    const handleProfileModalConfirm = useCallback(
        (profile: AnonymousProfile, enabled: boolean) => {
            setProfile(profile, enabled);
            setShowProfileModal(false);
        },
        [setProfile]
    );

    // 로그인 모달 열기 전 URL 저장하는 핸들러
    const handleSignIn = useCallback(() => {
        saveContentUrlForRedirect();
        openSignInModal();
    }, [saveContentUrlForRedirect, openSignInModal]);

    return (
        <div className={cn("py-6", className)}>
            {!hideTopDivider && <Divider className="mb-6" />}

            <AnonymousProfileToggle
                isLoggedIn={isLoggedIn}
                isEnabled={isAnonymousModeEnabled}
                totalCount={totalCount}
                onToggle={toggleEnabled}
                onProfileUpdate={setProfile}
                onSignIn={handleSignIn}
            />

            <CommentInput
                isLoggedIn={isLoggedIn}
                isAnonymousMode={isAnonymousModeEnabled}
                anonymousProfile={anonymousProfile}
                user={me}
                isSubmitting={false}
                voteId={contentId}
                onSubmit={handleSubmitComment}
                onRegenerateProfile={regenerateProfile}
                onSignIn={handleSignIn}
            />

            <ContentCommentList
                comments={commentTree}
                isLoading={isLoading}
                myUserId={me?.id}
                isAnonymousMode={isAnonymousModeEnabled}
                anonymousProfile={anonymousProfile}
                user={me}
                isPending={isPending}
                getSecondsRemaining={getSecondsRemaining}
                onReply={handleSubmitReply}
                onDeleteComment={scheduleDelete}
                onRevertDelete={revertDelete}
                onToggleReaction={handleToggleReaction}
                reactionLoadingId={null}
                onSignIn={handleSignIn}
            />

            <AnonymousProfileModal
                isOpen={showProfileModal}
                onClose={() => setShowProfileModal(false)}
                onConfirm={handleProfileModalConfirm}
            />
        </div>
    );
};
